import { useMemo } from "react";

import type { FilterType } from "@/components/@types";
import {
  buildWidgetHeaderFilterProps,
  resolveEffectiveWidgetFilters,
  type ReportLayoutWidget,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { useWidgetReportFilterDefinitions } from "@/app/app/_/views/ReportsView/reportFilterDefinitions.hooks";
import { useReportsViewWidgetFilterOverride } from "@/app/app/_/views/ReportsView/ReportsViewWidgetFilterOverridesContext";
import { State } from "@/state";

export function useReportsViewWidgetEffectiveFilters(
  widget: ReportLayoutWidget,
): ReturnType<typeof resolveEffectiveWidgetFilters> {
  const globalFilters = State((state) => state.filters);
  const filterDefinitions = useWidgetReportFilterDefinitions(
    widget.source,
  );
  const localOverride = useReportsViewWidgetFilterOverride(widget.id);

  return useMemo(
    () =>
      resolveEffectiveWidgetFilters(
        widget,
        globalFilters,
        filterDefinitions,
        localOverride,
      ),
    [widget, globalFilters, filterDefinitions, localOverride],
  );
}

export function useReportsViewWidgetHeaderFilters(
  widget: ReportLayoutWidget | undefined,
  onFiltersChange: (filters: FilterType[]) => void,
) {
  const filterDefinitions = useWidgetReportFilterDefinitions(
    widget?.source,
  );
  const localOverride = useReportsViewWidgetFilterOverride(widget?.id);

  return useMemo(() => {
    if (!widget) return null;
    const props = buildWidgetHeaderFilterProps(
      widget,
      filterDefinitions,
      onFiltersChange,
    );
    if (!props) return null;
    if (localOverride != null) {
      return { ...props, filters: localOverride };
    }
    return props;
  }, [widget, filterDefinitions, onFiltersChange, localOverride]);
}

export function useReportsViewWidgetFilterDefinitions(
  widget: ReportLayoutWidget | undefined,
) {
  return useWidgetReportFilterDefinitions(widget?.source);
}
